"use client";

import React, { useState, useRef, useEffect } from "react";
import { FaAngleDown } from "@/components/icons";

// Base input props
interface BaseInputProps {
  label?: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
  error?: string;
}

// Text input props
interface TextInputProps extends BaseInputProps {
  type?: "text" | "email" | "password" | "tel" | "url";
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
}

// Textarea props
interface TextareaProps extends BaseInputProps {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  name?: string;
  rows?: number;
}

// Select props
interface SelectProps extends BaseInputProps {
  options: { value: string; label: string }[];
  value?: string;
  onChange?: (e: { target: { name?: string; value: string } }) => void;
  name?: string;
}

// Text Input Component
export function FormInput({
  type = "text",
  placeholder,
  required = false,
  className = "",
  value,
  onChange,
  name,
  error,
}: TextInputProps): React.ReactElement {
  return (
    <div className={`relative ${className}`}>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-[25px] py-3 sm:py-4 bg-foreground/4 border border-foreground/5 text-foreground text-base font-kode-mono placeholder:text-foreground/60 focus:bg-accent-gold/13 focus:outline-none focus:border-accent-gold/36 transition-colors"
      />
      {required && (
        <span className="absolute left-[14px] top-[12px] text-accent-red/60 text-[16px] pointer-events-none">
          *
        </span>
      )}
      {error && (
        <span className="text-accent-red text-[11px] font-kode-mono mt-1 block">{error}</span>
      )}
    </div>
  );
}

// Textarea Component
export function FormTextarea({
  placeholder,
  required = false,
  className = "",
  value,
  onChange,
  name,
  rows = 5,
  error,
}: TextareaProps): React.ReactElement {
  return (
    <div className={`relative ${className}`}>
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-[25px] py-[16px] bg-foreground/4 border border-foreground/5 text-foreground text-[16px] font-kode-mono placeholder:text-foreground/60 focus:bg-accent-gold/13 focus:outline-none focus:border-accent-gold/36 transition-colors resize-none"
      />
      {required && (
        <span className="absolute left-[14px] top-[12px] text-accent-red/60 text-[16px] pointer-events-none">
          *
        </span>
      )}
      {error && (
        <span className="text-accent-red text-[11px] font-kode-mono mt-1 block">{error}</span>
      )}
    </div>
  );
}

// Select Component
export function FormSelect({
  placeholder,
  required = false,
  className = "",
  options,
  value,
  onChange,
  name,
  error,
}: SelectProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : "";

  const handleSelect = (optionValue: string) => {
    if (onChange) {
      onChange({ target: { name, value: optionValue } });
    }
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Required Asterisk */}
      {required && (
        <span className="absolute left-[14px] top-[12px] text-accent-red/60 text-[16px] pointer-events-none z-10">
          *
        </span>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-[25px] cursor-pointer py-[16px] bg-foreground/4 border border-foreground/5 text-[16px] font-kode-mono transition-colors text-left flex items-center justify-between ${value ? "text-foreground" : "text-foreground/60"
          } ${isOpen
            ? "bg-accent-gold/13 border-accent-gold/36"
            : "hover:bg-foreground/6 focus:bg-accent-gold/13 focus:border-accent-gold/36"
          }`}
      >
        <span className={required ? "" : ""}>
          {displayText || placeholder || ""}
        </span>
        <FaAngleDown
          className={`text-foreground/60 text-[14px] transition-transform shrink-0 ml-2 ${isOpen ? "rotate-180" : ""
            }`}
        />
      </button>

      {/* Dropdown Menu */}
      <div
        className={`absolute z-50 w-full mt-[4px] bg-foreground/4 border border-foreground/5 shadow-lg backdrop-blur-xs transition-all duration-300 ease-in-out ${isOpen
            ? "opacity-100 translate-y-0 visible pointer-events-auto"
            : "opacity-0 -translate-y-2 invisible pointer-events-none"
          }`}
      >
        <div className="max-h-[240px] overflow-y-auto custom-scrollbar">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={`w-full cursor-pointer px-[25px] py-[16px] text-left text-[16px] font-kode-mono transition-colors ${value === option.value
                ? "bg-accent-gold/13 text-accent-gold"
                : "text-foreground hover:bg-accent-gold/13"
                }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <span className="text-accent-red text-[11px] font-kode-mono mt-1 block">{error}</span>
      )}
    </div>
  );
}
